import { Link } from "react-router-dom";

export function KpiTile({ label, value, color, Icon, testId }) {
  return (
    <div className="flat-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon size={18} strokeWidth={1.5} color={color} />
      </div>
      <div className="font-mono text-3xl mt-3" style={{ color }}>{value}</div>
    </div>
  );
}

export function EnrollmentItem({ enrollment }) {
  const e = enrollment;
  return (
    <Link
      to={`/courses/${e.course_id}`}
      className="block border border-[var(--border)] p-4 hover:border-[var(--cyan)] transition-colors"
      data-testid={`enroll-${e.course?.code}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-xs text-[var(--cyan)]">{e.course?.code}</div>
          <div className="font-display font-bold text-lg uppercase">{e.course?.title}</div>
          <div className="text-xs text-[var(--text-2)] mt-1 uppercase tracking-widest font-mono">
            {e.course?.stage} &middot; {e.course?.duration_hours}h
          </div>
        </div>
        <span className={`tag ${e.status === "completed" ? "tag-green" : "tag-cyan"}`}>{e.status}</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="progress-track flex-1">
          <div className="progress-fill" style={{ width: `${e.progress}%` }} />
        </div>
        <div className="font-mono text-sm w-10 text-right">{e.progress}%</div>
      </div>
    </Link>
  );
}

export function UpcomingDrills({ exercises }) {
  return (
    <div className="flat-card p-6">
      <div className="overline">// 02_EXERCISES</div>
      <h2 className="font-display font-bold text-2xl uppercase mt-1 mb-5">Upcoming Drills</h2>
      <div className="space-y-3">
        {exercises.map((x) => (
          <div key={x.id} className="border border-[var(--border)] p-3 hover:border-[var(--cyan)] transition-colors" data-testid={`ex-${x.code}`}>
            <div className="font-mono text-xs text-[var(--cyan)]">{x.code}</div>
            <div className="font-display font-bold uppercase">{x.title}</div>
            <div className="font-mono text-xs text-[var(--text-2)] mt-1">
              {new Date(x.scheduled_at).toLocaleDateString()} &middot; {x.difficulty}
            </div>
          </div>
        ))}
        <Link to="/exercises" className="btn-ghost inline-flex items-center gap-2 mt-2" data-testid="dash-all-exercises">
          All exercises →
        </Link>
      </div>
    </div>
  );
}

export function ForceSnapshot({ metrics }) {
  return (
    <div className="flat-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">// 03_FORCE_READINESS</div>
          <h2 className="font-display font-bold text-2xl uppercase mt-1">Force-Wide Snapshot</h2>
        </div>
        <Link to="/metrics" className="text-xs font-mono tracking-widest uppercase text-[var(--cyan)] hover:underline" data-testid="dash-metrics-link">Full Readiness →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Qualification" v={`${metrics.qualification_rate}%`} />
        <Stat label="Time-to-Ready" v={`${metrics.time_to_mission_ready_days}d`} />
        <Stat label="Exercise Perf" v={`${metrics.exercise_performance}%`} />
        <Stat label="Retention" v={`${metrics.retention}%`} />
      </div>
    </div>
  );
}

export function RecertAlert({ data }) {
  if (!data) return null;
  const pending = data.pending_certs || [];
  const queued = data.recertification_enrollments || [];
  if (pending.length === 0 && queued.length === 0) return null;

  const hasExpired = pending.some((p) => p.expiry_status === "expired");
  const accent = hasExpired ? "var(--red)" : "var(--amber)";

  return (
    <div className="flat-card p-6 border-l-4" style={{ borderLeftColor: accent }} data-testid="recert-alert">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: accent }}>// RECERTIFICATION_NOTICE</div>
          <h2 className="font-display font-bold text-xl uppercase mt-1">
            {hasExpired ? "Qualifications Expired" : "Recertification Window Open"}
          </h2>
        </div>
        <span
          className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 border"
          style={{ color: accent, borderColor: accent }}
          data-testid="recert-count"
        >
          {pending.length} CERT{pending.length === 1 ? "" : "S"}
        </span>
      </div>
      <div className="space-y-2">
        {pending.slice(0, 4).map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between border border-[var(--border)] px-3 py-2"
            data-testid={`recert-cert-${p.id}`}
          >
            <div className="font-mono text-xs text-[var(--text-2)]">
              <span className="text-[var(--cyan)]">CERT</span> / {p.course_id?.slice(0, 8)}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-[var(--text-2)]" data-testid={`recert-days-${p.id}`}>
                {p.expiry_status === "expired"
                  ? `${Math.abs(p.days_remaining || 0).toFixed(0)}d overdue`
                  : `${(p.days_remaining || 0).toFixed(0)}d left`}
              </span>
              <span
                className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border"
                style={{
                  color: p.expiry_status === "expired" ? "var(--red)" : "var(--amber)",
                  borderColor: p.expiry_status === "expired" ? "var(--red)" : "var(--amber)",
                }}
              >
                {p.expiry_status}
              </span>
            </div>
          </div>
        ))}
      </div>
      {queued.length > 0 && (
        <div className="mt-3 font-mono text-xs text-[var(--text-2)]">
          Sentinel auto-enrolled you in{" "}
          <span className="text-[var(--cyan)]" data-testid="recert-queued">
            {queued.length} recertification course{queued.length === 1 ? "" : "s"}
          </span>
          . Continue from the Courses page.
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="border border-[var(--border)] p-4">
      <div className="overline">{label}</div>
      <div className="font-mono text-2xl text-[var(--cyan)] mt-1">{v}</div>
    </div>
  );
}
